import { useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { useViewMode } from '@/lib/view-mode-context';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Plus, Trash2, Edit2, Check, X, User, Camera, Loader2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { calculatePlayerStats } from '@/lib/player-stats';
import { cn } from '@/lib/utils';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { PlayerAvatar } from '@/components/PlayerAvatar';
import { usePlayers, useCreatePlayer, useUpdatePlayer, useDeletePlayer } from '@/hooks/use-players';
import { useAllSessionPlayers } from '@/hooks/use-session-players';
import { useExpenses } from '@/hooks/use-expenses';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

/* ── Main page ───────────────────────────────────────────── */
export default function Players() {
  const { user } = useAuth();
  const { targetUserId, isReadOnly } = useViewMode();
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [nickname, setNickname] = useState('');
  const [editing, setEditing] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [editNickname, setEditNickname] = useState('');
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const [uploadingId, setUploadingId] = useState<string | null>(null);

  const { data: playersRaw = [], isLoading: playersLoading } = usePlayers(targetUserId);
  const { data: spData = [], isLoading: spLoading } = useAllSessionPlayers(targetUserId);
  const { data: expensesData = [], isLoading: expensesLoading } = useExpenses(targetUserId);

  const createPlayerMutation = useCreatePlayer();
  const updatePlayerMutation = useUpdatePlayer();
  const deletePlayerMutation = useDeletePlayer();

  const loading = playersLoading || spLoading || expensesLoading;

  const playersWithStats = useMemo(() => {
    if (loading) return [];
    const expenseMap: Record<string, number> = {};
    expensesData.forEach((exp) => {
      if (!expenseMap[exp.payer_id]) expenseMap[exp.payer_id] = 0;
      expenseMap[exp.payer_id] += Number(exp.amount);
      exp.participants?.forEach((p: any) => {
        if (!expenseMap[p.player_id]) expenseMap[p.player_id] = 0;
        expenseMap[p.player_id] -= Number(p.share);
      });
    });
    return playersRaw.map((p) => {
      const pSessions = spData
        .filter((sp) => sp.player_id === p.id)
        .map((sp) => ({ net: sp.net_amount || 0, buyIns: sp.buy_ins, buyInUnit: (sp.sessions as any)?.buy_in_unit }));
      const stats = calculatePlayerStats(pSessions, expenseMap[p.id] || 0);
      return { ...p, rank: stats.rank, rankColor: stats.rankColor, totalNet: stats.totalNet };
    });
  }, [loading, playersRaw, spData, expensesData]);

  const addPlayer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !name.trim()) return;
    await createPlayerMutation.mutateAsync({ name: name.trim(), nickname: nickname.trim() || null, created_by: user.id });
    setName('');
    setNickname('');
  };

  const deletePlayer = async () => {
    if (!deleteTargetId || !user) return;
    await deletePlayerMutation.mutateAsync({ id: deleteTargetId, userId: targetUserId! });
    setDeleteTargetId(null);
  };

  const saveEdit = async (id: string) => {
    await updatePlayerMutation.mutateAsync({ id, name: editName.trim(), nickname: editNickname.trim() || null });
    setEditing(null);
  };

  const handleAvatarUpload = async (player: typeof playersWithStats[0], file: File) => {
    if (!user) return;
    setUploadingId(player.id);
    try {
      const ext = file.type === 'image/png' ? 'png' : file.type === 'image/webp' ? 'webp' : 'jpg';
      const path = `${user.id}/${player.id}.${ext}`;

      // Remove old file first (ignore errors)
      await supabase.storage.from('player-avatars').remove([path]).catch(() => {});

      const { error: uploadError } = await supabase.storage
        .from('player-avatars')
        .upload(path, file, { contentType: file.type, upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage.from('player-avatars').getPublicUrl(path);

      await updatePlayerMutation.mutateAsync({
        id: player.id,
        name: player.name,
        nickname: player.nickname || null,
        avatar_url: publicUrl,
      });

      toast.success('Photo updated!');
    } catch (err: any) {
      toast.error(`Upload failed: ${err.message}`);
    } finally {
      setUploadingId(null);
    }
  };

  return (
    <div className="py-6 space-y-6">
      <h1 className="text-xl font-bold tracking-tight">Player Roster</h1>

      {!isReadOnly && (
        <form onSubmit={addPlayer} className="flex gap-2">
          <Input placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} className="h-11 flex-1" required />
          <Input placeholder="Nickname" value={nickname} onChange={(e) => setNickname(e.target.value)} className="h-11 w-28" />
          <Button type="submit" size="icon" className="h-11 w-11 shrink-0"><Plus className="h-5 w-5" /></Button>
        </form>
      )}

      {loading && playersWithStats.length === 0 ? (
        <div className="py-12 flex justify-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : playersWithStats.length === 0 ? (
        <div className="text-center py-12 space-y-2">
          <User className="h-10 w-10 mx-auto text-muted-foreground/50" />
          <p className="text-muted-foreground">{isReadOnly ? 'No players found.' : 'No players yet. Add your first player above!'}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {playersWithStats.map((player) => (
            <Card key={player.id} className="border-0 shadow-sm" role="listitem">
              <CardContent className="p-3 flex items-center gap-3">
                {editing === player.id ? (
                  <>
                    <PlayerAvatar name={editName || player.name} avatarUrl={player.avatar_url} size={40} />
                    <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="h-9 flex-1" />
                    <Input value={editNickname} onChange={(e) => setEditNickname(e.target.value)} placeholder="Nickname" className="h-9 w-24" />
                    <Button size="icon" variant="ghost" className="h-9 w-9" onClick={() => saveEdit(player.id)}><Check className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="h-9 w-9" onClick={() => setEditing(null)}><X className="h-4 w-4" /></Button>
                  </>
                ) : (
                  <>
                    <PlayerAvatar
                      name={player.name}
                      avatarUrl={player.avatar_url}
                      size={40}
                      editable={!isReadOnly}
                      uploading={uploadingId === player.id}
                      onUpload={(file) => handleAvatarUpload(player, file)}
                      onClick={() => navigate(isReadOnly ? `/public/${targetUserId}/player/${player.id}` : `/player/${player.id}`)}
                    />
                    <div
                      className="flex-1 min-w-0 cursor-pointer"
                      onClick={() => navigate(isReadOnly ? `/public/${targetUserId}/player/${player.id}` : `/player/${player.id}`)}
                    >
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-sm truncate">{player.name}</p>
                        <Badge variant="outline" className={cn('text-[10px] h-4 px-1 font-bold', player.rankColor)}>{player.rank}</Badge>
                      </div>
                      {player.nickname && <p className="text-xs text-muted-foreground truncate">"{player.nickname}"</p>}
                    </div>
                    {!isReadOnly && (
                      <>
                        <Button size="icon" variant="ghost" className="h-9 w-9 text-muted-foreground" onClick={() => { setEditing(player.id); setEditName(player.name); setEditNickname(player.nickname || ''); }} data-testid="edit-player">
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" className="h-9 w-9 text-destructive" onClick={() => setDeleteTargetId(player.id)} data-testid="delete-player">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={deleteTargetId !== null}
        onOpenChange={(open) => { if (!open) setDeleteTargetId(null); }}
        title="Delete Player"
        description="Are you sure you want to delete this player? This cannot be undone."
        confirmLabel="Delete"
        onConfirm={deletePlayer}
      />
    </div>
  );
}
